/**
 * HTTP client. The token lives in localStorage; on a 401 the session is
 * considered expired and the user is sent to the login page, wherever they
 * happen to be in the application.
 */

const TOKEN_KEY = 'tmt.token';

export const getToken = (): string | null => localStorage.getItem(TOKEN_KEY);
export const setToken = (t: string): void => localStorage.setItem(TOKEN_KEY, t);
export const clearToken = (): void => localStorage.removeItem(TOKEN_KEY);

export class ApiError extends Error {
  constructor(readonly status: number, message: string) { super(message); }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = getToken();
  const headers = new Headers(init.headers);
  if (token) headers.set('authorization', `Bearer ${token}`);
  if (init.body && !(init.body instanceof FormData)) headers.set('content-type', 'application/json');

  const res = await fetch(`/api${path}`, { ...init, headers });

  if (res.status === 401) {
    clearToken();
    if (!location.pathname.endsWith('/login')) location.href = '/login';
    throw new ApiError(401, 'session expired');
  }
  if (!res.ok) {
    const detail = await res.json().catch(() => ({ error: res.statusText }));
    throw new ApiError(res.status, (detail as { error?: string }).error ?? 'error');
  }
  const type = res.headers.get('content-type') ?? '';
  return (type.includes('json') ? res.json() : res.text()) as Promise<T>;
}

export const api = {
  login: (email: string, password: string) =>
    request<{ token: string; user: { email: string; role: string } }>(
      '/auth/login', { method: 'POST', body: JSON.stringify({ email, password }) }),

  campaigns: () => request<Campaign[]>('/campaigns'),
  campaign: (id: number) => request<Campaign>(`/campaigns/${id}`),
  updateCampaign: (id: number, body: unknown) =>
    request<Campaign>(`/campaigns/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  removeCampaign: (id: number) =>
    request<{ deleted: number }>(`/campaigns/${id}`, { method: 'DELETE' }),
  createCampaign: (body: unknown) =>
    request<Campaign>('/campaigns', { method: 'POST', body: JSON.stringify(body) }),
  uploadNumbers: (id: number, file: File) => {
    const form = new FormData();
    form.append('file', file);
    return request<ImportSummary>(`/campaigns/${id}/numbers`, { method: 'POST', body: form });
  },
  deleteCampaign: (id: number) =>
    request<{ deleted: number }>(`/campaigns/${id}`, { method: 'DELETE' }),
  /** Numbers typed directly rather than uploaded — the same guards as the CSV. */
  addNumbers: (id: number, numbers: string) =>
    request<{ read: number; inserted: number; rejected: { number: string; reason: string | null }[] }>(
      `/campaigns/${id}/numbers/manual`, { method: 'POST', body: JSON.stringify({ numbers }) }),
  /** Puts the numbers back in the queue. Without `disposition` it re-runs the whole batch. */
  reset: (id: number, disposition?: string) =>
    request<{ requeued: number; recordingsDeleted: number }>(
      `/campaigns/${id}/reset`, { method: 'POST', body: JSON.stringify({ disposition }) }),
  cost: (id: number) => request<Cost>(`/campaigns/${id}/cost`),
  kpi: (id: number) => request<Kpi>(`/campaigns/${id}/kpi`),
  clone: (id: number, name?: string, withNumbers = true) =>
    request<Campaign & { numbersCopied: number }>(
      `/campaigns/${id}/clone`, { method: 'POST', body: JSON.stringify({ name, withNumbers }) }),
  compare: (a: number, b: number) => request<Comparison>(`/compare?a=${a}&b=${b}`),
  suppress: (msisdn: string, reason?: string) =>
    request<{ msisdn: string }>('/suppression', { method: 'POST', body: JSON.stringify({ msisdn, reason }) }),
  campaignAction: (id: number, action: 'start' | 'pause' | 'stop') =>
    request<{ status: string }>(`/campaigns/${id}/${action}`, { method: 'POST' }),
  results: (id: number, disposition?: string) =>
    request<Result[]>(`/campaigns/${id}/results${disposition ? `?disposition=${disposition}` : ''}`),
  stats: (id: number) => request<Stat[]>(`/campaigns/${id}/stats`),

  messages: (lang?: string) => request<Message[]>(`/messages${lang ? `?lang=${lang}` : ''}`),
  createMessage: (body: { name?: string; text: string; lang: string; voice: string }) =>
    request<Message & { cached: boolean; maxTalkSeconds: number }>(
      '/messages', { method: 'POST', body: JSON.stringify(body) }),

  deleteMessage: (id: number) =>
    request<{ deleted: number }>(`/messages/${id}`, { method: 'DELETE' }),
  createSilence: (seconds: number) =>
    request<Message & { cached: boolean; maxTalkSeconds: number }>(
      '/messages/silence', { method: 'POST', body: JSON.stringify({ seconds }) }),
  inboundGreeting: () => request<{ text: string; voice: string }>('/settings/inbound-greeting'),
  saveInboundGreeting: (text: string, voice: string) =>
    request<{ durationMs: number; cached: boolean }>(
      '/settings/inbound-greeting', { method: 'POST', body: JSON.stringify({ text, voice }) }),
  changePassword: (currentPassword: string, newPassword: string) =>
    request<{ ok: true }>('/auth/password',
      { method: 'POST', body: JSON.stringify({ currentPassword, newPassword }) }),
  trunks: () => request<Trunk[]>('/trunks'),
  rules: () => request<Rule[]>('/disposition-rules'),
};

/**
 * Downloads a file from a protected route.
 *
 * A plain `<a href>` would have the same problem as `<audio src>`: the browser
 * does not send the token and the request fails with 401. We fetch the content
 * and trigger the download from a local blob.
 */
export async function download(path: string, filename: string): Promise<void> {
  const res = await fetch(`/api${path}`, {
    headers: { authorization: `Bearer ${getToken() ?? ''}` },
  });
  if (!res.ok) throw new ApiError(res.status, 'download failed');

  const url = URL.createObjectURL(await res.blob());
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

/**
 * The protected audio paths. They are NOT put directly into `<audio src>`: the
 * browser does not attach the authorization header there and the request gets a
 * 401. They are used through the AudioPlayer component, which fetches them
 * authenticated.
 */
export const audioUrl = {
  message: (sha: string) => `/api/messages/${sha}/audio`,
  recording: (callId: string) => `/api/recordings/${callId}`,
};

// ── Response types (the API returns raw columns for some resources) ──────────

export interface Campaign {
  id: number; name: string; country: string; lang: string;
  cli: string; status: string; concurrency: number; calls_per_min: number;
  ring_timeout: number; allowed_prefix: string; trunk_id: number; tts_message_id: number;
  cost_per_min?: string | number; notes?: string | null;
  trunk_name?: string; message_name?: string; total?: string; completed?: string;
}

export interface Result {
  msisdn: string; state: string; skip_reason: string | null;
  call_id: string | null; disposition: string | null; disposition_reason: string | null;
  confidence: string | null; dialstatus: string | null;
  sip_code: number | null; sip_reason: string | null; q850_cause: number | null;
  pdd_ms: number | null; early_media: boolean | null;
  audio_pattern: string | null; audio_duration_s: string | null;
  billable_s: string | null; has_recording: boolean; created_at: string | null;
}

export interface Kpi {
  campaign: { id: number; name: string; country: string; status: string };
  volume: { total: number; called: number; remaining: number; skipped: number;
            answered: number; withEarlyMedia: number };
  telecom: { asr: number; ner: number; acd: number;
             pddMedian: number | null; pddP95: number | null };
  validation: { active: number; notAllocated: number; validityRate: number; phoneOff: number };
  operations: { durationMinutes: number | null; actualCallsPerMinute: number | null;
                networkErrorRate: number; totalCost: number; costPerNumberTested: number };
  distribution: { disposition: string; n: number; percent: number }[];
  sipCodes: { code: number | null; reason: string | null; n: number }[];
  pddBuckets: { bucket: string; n: number }[];
}

export interface Comparison {
  campaigns: { a: number; b: number };
  total: number; changed: number; unchanged: number; onlyInA: number; onlyInB: number;
  transitions: { transition: string; n: number }[];
  items: { msisdn: string; a: string | null; b: string | null;
           whenA: string | null; whenB: string | null; state: string }[];
}

export interface Cost {
  ratePerMinute: number; called: number; answered: number; remaining: number;
  billedSeconds: number; spent: number; secondsPerAnsweredCall: number;
  answerRateUsed: number; estimatedRemaining: number; estimatedTotal: number;
}

export interface Stat {
  disposition: string; label: string; n: number;
  avg_pdd_ms: string | null; billed_seconds: string | null;
}

export interface Message {
  id: number; name: string; text: string; lang: string; voice: string;
  sha256: string; durationMs: number | null; createdAt: string;
}

export interface Trunk { id: number; name: string; dids: string[]; sbc_host: string }

/** The import result: how many numbers passed the guards and why the rest failed. */
export interface ImportSummary {
  read: number;
  inserted: number;
  summary: Record<string, number>;
}

export interface Rule {
  id: number; priority: number; matchDialstatus: string | null;
  matchSip: number | null; matchQ850: number | null;
  matchAudioPattern: string | null; disposition: string; note: string | null;
}
